<template>
    <left-side-filters
        :filters="filters"
        :module-name="moduleName"
        @apply-filters="applyFilters"
    >
        <template #header>
            <bread-crumbs />
        </template>

        <template #content>
            <div class="c-settings flex-row flex-wrap pt-1 gap-1">
                <h4 class="typo typo--h2" v-text="'Reklamacje'" />

                <results-per-page
                    class="ml-auto"
                    :per-page="pagination.perPage"
                    :on-change="changeResultsPerPage"
                    :options="[5, 10, 25]"
                    text="Pokazuj na stronie:"
                />

                <sort-component
                    :module-name="moduleName"
                    :options="sortOptions"
                    :current-sort="currentSort"
                    @on-change="fetch"
                />
            </div>

            <selected-filters
                class="mb-4"
                :module-name="moduleName"
                :filters="filters"
                @applyFilters="applyFilters"
            />

            <page-preloader v-if="status === 'loading'" />

            <template v-else-if="reclamations.list.length > 0">
                <reclamation-preview
                    v-for="reclamation in reclamations.list"
                    :reclamation="reclamation"
                    :key="reclamation.id"
                />

                <the-pagination
                    class="mt-4"
                    :pagination="pagination"
                    @change="onPaginationChange"
                />
            </template>

            <div v-else-if="!isLocationIdSelected">
                Wybierz sklep w filtrze, aby wyświetlić raport
            </div>

            <div v-else v-text="'Brak wyników'" />
        </template>
    </left-side-filters>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { registerPageModule } from '@/utils/store'
import RetrReclamationsStore from '@/store/modules/my-business/retr-reclamations.module'
import LeftSideFilters from '@/components/layout/LeftSideFilters.vue'
import ReclamationPreview from '@/components/my-business/retr/reclamations/ReclamationPreview.vue'
import networkMixin from './network.mixin'

export default {
    mixins: [networkMixin],
    name: 'Reclamations',

    data() {
        return {
            title: 'Reclamations',
            moduleName: 'retrReclamations',
            locationId: null
        }
    },

    beforeCreate() {
        registerPageModule(this.$store, RetrReclamationsStore, 'retrReclamations')

        // Force update getLocationsByNetworkAlias getter
        this.$store.commit('retrReclamations/SET_ALIAS', this.$route.params.alias)
    },

    computed: {
        ...mapState('retrReclamations', ['sortOptions', 'currentSort']),

        ...mapGetters('retrReclamations', [
            'reclamations',
            'filters',
            'retr',
            'status',
            'canComplaint'
        ]),

        isLocationIdSelected() {
            return !!this.filters.locationId.value
        },

        pagination() {
            const { count, limit, offset, total } = this.reclamations.resultSet
            const page = Math.floor(offset / limit) + 1

            return {
                perPage: limit,
                nextPage: offset + count < total ? page + 1 : null,
                prevPage: offset > 0 ? page - 1 : null,
                page,
                totalPages: Math.ceil(total / limit),
                totalRecords: total
            }
        }
    },

    methods: {
        ...mapActions('retrReclamations', [
            'loadReclamations',
            'setDefaultLocationId',
            'loadContractors',
            'resetContractors'
        ]),

        fetch({ page } = {}) {
            this.loadReclamations({ page })
        },

        async applyFilters({ filter, value }) {
            if (filter === 'locationId') {
                // If locationId changed, reset contractors
                if (this.filters.locationId.value !== value) {
                    await this.resetContractors()
                    await this.loadContractors()
                }
            }

            this.fetch()

            this.locationId = this.filters.locationId.value
        },

        onPaginationChange(page) {
            this.fetch({ page })

            this.$scrollTo(document.querySelector('.site-header'), 100)
        },

        changeResultsPerPage(limit) {
            this.$store.commit('retrReclamations/SET_LIMIT', limit)
            this.fetch({ page: 1 })
        }
    },

    async created() {
        if (this.$store.getters.isShopUser) {
            // Load networks and check network permission
            await this.loadNetworksAndCheckPermission()
            if (!this.isShopUserNetworkPermission) {
                return
            }
        }

        this.setDefaultLocationId()

        // Reset contractors
        this.resetContractors()
        this.loadContractors()

        this.fetch()
    },

    components: {
        LeftSideFilters,
        ReclamationPreview
    }
}
</script>